import React, { useLayoutEffect } from 'react';
import { Sidebar, Menu, MenuItem, SidebarHeader, SidebarFooter, SidebarContent } from './Sidebar';
import logoSvg from 'src/assets/cydea.svg';
import styled from 'styled-components';
import { useSidebar } from 'src/state/sidebar';
import { useAuth } from 'src/state/auth';
import { Poppins } from '../../../common';
import _ from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AuditLog,
  ControlLib,
  Help,
  Incid,
  Insights,
  ReportCentre,
  RiskAssessments,
  RiskMng,
  ScenLib,
  Settings,
} from './icons';
import { useAssessment } from 'src/state/assessment';
import { Timeline } from './icons/Timeline';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Chevron } from './Sidebar/Chevron';
import { useImageLightness } from 'src/hooks/useImageLightness';

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;
`;

const LogoWrap = styled.div`
  margin-left: 8px;
`;

const LogoImage = styled.img<{ $shadow?: boolean }>`
  width: 100px;
  height: 38px;
  margin-left: 8px;
  object-fit: contain;
  filter: ${({ $shadow }) =>
    $shadow ? 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3)) drop-shadow(0 0 1px rgba(0, 0, 0, 0.25))' : 'none'};

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 80px;
    height: 30px;
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 60px;
    height: 22px;
  }
`;

const PoweredBy = styled.div`
  position: absolute;
  top: calc(100% - 2px);
  left: 16px;
  height: 22px;
  display: flex;
  align-items: center;
`;

const PoweredByText = styled(Poppins)`
  font-size: 8px;
  line-height: 22px;
  font-weight: 400;
  background: linear-gradient(180deg, #e62e61 0%, #fd5630 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`;

const Aside = () => {
  const { collapsed, setCollapsed } = useSidebar();
  const { features, profile, workspaceId } = useAuth();
  const { setPage } = useAssessment();
  const navigate = useNavigate();
  const isSm = useBreakpoints('sm');
  const { pathname } = useLocation();

  const isWorkspaceMenu = !/^\/home$|^\/organisation(\/[^/]*)?$/.test(pathname) && workspaceId;
  const orgIdPath = `/${profile?.activeOrganisation?.id}`;
  const canAccess = profile?.activeWorkspace?.actionsOnThisWorkspace?.canAccess;
  const canSeeWorkspaceSettings = profile?.activeWorkspace?.actionsOnThisWorkspace?.canSeeSettings;
  const canSeeOrganisationSettings = profile?.activeOrganisation?.actionsOnThisOrganisation?.canSeeSettings;
  const partnerLogoUrl = profile?.activeOrganisation?.logoUrl;
  const logoSrc = partnerLogoUrl || logoSvg;
  const partnerLogoIsLight = useImageLightness(partnerLogoUrl);
  const shouldShadowLogo = Boolean(partnerLogoUrl) && (partnerLogoIsLight ?? true);

  useLayoutEffect(() => {
    if (isSm) {
      setCollapsed(true);
    }
  }, [isSm]);

  return (
    <Sidebar>
      <SidebarHeader>
        {!collapsed && (
          <LogoBlock
            onClick={() => {
              navigate('/home');
              mpEvent(MPEvents.LogoClick, {});
            }}
          >
            <LogoWrap>
              <LogoImage
                $shadow={shouldShadowLogo}
                src={logoSrc}
                alt={partnerLogoUrl ? 'Partner Logo' : 'Cydea Logo'}
              />
            </LogoWrap>
            {partnerLogoUrl && (
              <PoweredBy>
                <PoweredByText nowrap>Powered by Cydea</PoweredByText>
              </PoweredBy>
            )}
          </LogoBlock>
        )}
        <Chevron
          collapsed={collapsed}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setCollapsed(!collapsed);
            mpEvent(MPEvents.SideBarToggle, {
              value: collapsed ? 'open' : 'close',
            });
          }}
        />
      </SidebarHeader>
      {!isWorkspaceMenu ? (
        <SidebarContent>
          <Menu>
            {canSeeOrganisationSettings ? (
              <MenuItem path={`/organisation${orgIdPath}`} Icon={Settings} dataCy="sidemenu-org-settings">
                Settings
              </MenuItem>
            ) : (
              <></>
            )}
            {features['app.help.page'] ? (
              <MenuItem path="/get-started" Icon={Help}>
                Help
              </MenuItem>
            ) : (
              <></>
            )}
          </Menu>
        </SidebarContent>
      ) : (
        <SidebarContent>
          <Menu>
            {canAccess ? (
              <MenuItem path="/timeline" Icon={Timeline} dataCy="sidemenu-tl">
                Timeline
              </MenuItem>
            ) : (
              <></>
            )}
            {canAccess ? (
              <MenuItem path="/insights" Icon={Insights} dataCy="sidemenu-insights">
                Insights
              </MenuItem>
            ) : (
              <></>
            )}
            {canAccess ? (
              <MenuItem path="/risk-assessments" Icon={RiskAssessments} dataCy="sidemenu-ra" onClick={() => setPage(1)}>
                Risk Assessments
              </MenuItem>
            ) : (
              <></>
            )}
            {canAccess ? (
              <MenuItem path="/scenario-library" Icon={ScenLib} dataCy="sidemenu-sl">
                Scenario Library
              </MenuItem>
            ) : (
              <></>
            )}
            {canAccess ? (
              <MenuItem path="/control-frameworks" Icon={ControlLib} dataCy="sidemenu-cf">
                Control Frameworks
              </MenuItem>
            ) : (
              <></>
            )}
            {canAccess ? (
              <MenuItem path="/risk-management" Icon={RiskMng} dataCy="sidemenu-rm">
                Risk Management
              </MenuItem>
            ) : (
              <></>
            )}
            {canAccess ? (
              <MenuItem path="/incidents" Icon={Incid} dataCy="sidemenu-si">
                Security Incidents
              </MenuItem>
            ) : (
              <></>
            )}
            {canAccess ? (
              <MenuItem path="/report-centre" Icon={ReportCentre} dataCy="sidemenu-rc">
                Report Centre
              </MenuItem>
            ) : (
              <></>
            )}
            {canSeeWorkspaceSettings ? (
              <MenuItem path={`/workspace`} Icon={Settings} dataCy="sidemenu-workspace-settings">
                Settings
              </MenuItem>
            ) : (
              <></>
            )}
            {canAccess ? (
              <MenuItem path={`/audit/workspace`} Icon={AuditLog} dataCy="sidemenu-auw">
                Audit Log
              </MenuItem>
            ) : (
              <></>
            )}
            {features['app.help.page'] ? (
              <MenuItem path="/get-started" Icon={Help}>
                Help
              </MenuItem>
            ) : (
              <></>
            )}
          </Menu>
        </SidebarContent>
      )}
      <SidebarFooter>
        <Poppins px={14} weight={600} color="cflowerBlue" css="display: block" nowrap>
          Cydea Risk Assessment Tool
        </Poppins>
        <Poppins px={14} color="cflowerBlue" css="display: block" nowrap>
          © {new Date().getFullYear()} All Rights Reserved
        </Poppins>
      </SidebarFooter>
    </Sidebar>
  );
};

export default Aside;
